import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { useCallback, useEffect } from "react";

// Navigation actions
import {
  navigateToEntity,
  navigateBack,
  navigateForward as navigateForwardAction,
  navigateToHistoryIndex,
  syncWithRoute as syncWithRouteAction,
  updateEntityTab,
  setDirectNavigation,
  resetBreadcrumb,
  addToBreadcrumb,
  removeFromBreadcrumb,
  clearNavigation,
} from "../store/actions/navigation.actions";

// Navigation selectors
import {
  getBreadcrumbPath,
  getBreadcrumbHistory,
  getCurrentBreadcrumbIndex,
  getCurrentEntity,
  getEntityContexts,
  getBreadcrumbItems,
  canNavigateBack,
  canNavigateForward,
  getParentEntity,
  isAtDashboard,
  getEntitySelectedTab,
  getNavigationError,
  getNavigationLoading,
  getNextEntity,
  getPreviousEntity,
  getForwardNavigationOptions,
} from "../store/selectors/navigation.selector";

const useNavigation = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  // Get navigation state from Redux
  const breadcrumbPath = useSelector(getBreadcrumbPath);
  const breadcrumbHistory = useSelector(getBreadcrumbHistory);
  const currentBreadcrumbIndex = useSelector(getCurrentBreadcrumbIndex);
  const currentEntity = useSelector(getCurrentEntity);
  const entityContexts = useSelector(getEntityContexts);
  const breadcrumbItems = useSelector(getBreadcrumbItems);
  const canGoBack = useSelector(canNavigateBack);
  const canGoForward = useSelector(canNavigateForward);
  const parentEntity = useSelector(getParentEntity);
  const isOnDashboard = useSelector(isAtDashboard);
  const navigationError = useSelector(getNavigationError);
  const navigationLoading = useSelector(getNavigationLoading);
  const nextEntity = useSelector(getNextEntity);
  const previousEntity = useSelector(getPreviousEntity);
  const forwardOptions = useSelector(getForwardNavigationOptions);

  // Navigate to an entity with hierarchical logic
  const navigateTo = useCallback(
    (entityType, entityId, entityName, selectedTab) => {
      // Dispatch navigation action
      dispatch(navigateToEntity(entityType, entityId, entityName, selectedTab));

      // Navigate to the actual route
      const routeMap = {
        Clients: "clients",
        Portfolios: "portfolio",
        Programs: "program",
        Projects: "projects",
        Milestones: "scopes",
        ScopeItems: "scopes",
      };

      const routePath = routeMap[entityType] || entityType;
      const targetRoute =
        entityType === "dashboard"
          ? "/dashboard"
          : `/${routePath}/${entityId}/overview`;

      history.push(targetRoute);
    },
    [dispatch, history]
  );

  // Navigate back to a specific breadcrumb item
  const navigateBackTo = useCallback(
    (targetEntity) => {
      // Dispatch navigation action
      dispatch(navigateBack(targetEntity));

      // Navigate to the target route
      const routeMap = {
        Clients: "clients",
        Portfolios: "portfolio",
        Programs: "program",
        Projects: "projects",
        Milestones: "scopes",
        ScopeItems: "scopes",
      };

      const routePath = routeMap[targetEntity.type] || targetEntity.type;
      const targetRoute =
        targetEntity.type === "dashboard"
          ? "/dashboard"
          : `/${routePath}/${targetEntity.id}/overview`;

      history.push(targetRoute);
    },
    [dispatch, history, entityContexts]
  );

  // Navigate forward to the next entity in history
  const navigateForward = useCallback(() => {
    if (canGoForward) {
      dispatch(navigateForwardAction());
      
      // Navigate to the actual route
      if (nextEntity) {
        const routeMap = {
          Clients: "clients",
          Portfolios: "portfolio",
          Programs: "program",
          Projects: "projects",
          Milestones: "scopes",
          ScopeItems: "scopes",
        };

        const routePath = routeMap[nextEntity.type] || nextEntity.type;
        const targetRoute =
          nextEntity.type === "dashboard"
            ? "/dashboard"
            : `/${routePath}/${nextEntity.id}/overview`;

        history.push(targetRoute);
      }
    }
  }, [canGoForward, nextEntity, dispatch, history]);

  // Navigate to the previous entity in history
  const navigateBackward = useCallback(() => {
    if (previousEntity) {
      navigateBackTo(previousEntity);
    }
  }, [previousEntity, navigateBackTo]);

  // Navigate to a specific index in the history
  const navigateToIndex = useCallback(
    (index) => {
      if (index >= 0 && index < breadcrumbHistory.length) {
        dispatch(navigateToHistoryIndex(index));
        
        // Navigate to the actual route
        const targetEntity = breadcrumbHistory[index];
        if (targetEntity) {
          const routeMap = {
            Clients: "clients",
            Portfolios: "portfolio",
            Programs: "program",
            Projects: "projects",
            Milestones: "scopes",
            ScopeItems: "scopes",
          };

          const routePath = routeMap[targetEntity.type] || targetEntity.type;
          const targetRoute =
            targetEntity.type === "dashboard"
              ? "/dashboard"
              : `/${routePath}/${targetEntity.id}/overview`;

          history.push(targetRoute);
        }
      }
    },
    [breadcrumbHistory, dispatch, history]
  );

  // Update tab for current entity
  const updateTab = useCallback(
    (selectedTab) => {
      if (currentEntity) {
        dispatch(
          updateEntityTab(currentEntity.type, currentEntity.id, selectedTab)
        );
      }
    },
    [dispatch, currentEntity]
  );

  // Navigate directly to an entity (from dashboard)
  const navigateDirectly = useCallback(
    (entityType, entityId, entityName, selectedTab) => {
      // Dispatch direct navigation action
      dispatch(
        setDirectNavigation(entityType, entityId, entityName, selectedTab)
      );

      // Navigate to the actual route
      const routeMap = {
        Clients: "clients",
        Portfolios: "portfolio",
        Programs: "program",
        Projects: "projects",
        Milestones: "scopes",
        ScopeItems: "scopes",
      };

      const routePath = routeMap[entityType] || entityType;
      const targetRoute = `/${routePath}/${entityId}/overview`;

      history.push(targetRoute);
    },
    [dispatch, history]
  );

  // Navigate to dashboard
  const navigateToDashboard = useCallback(() => {
    dispatch(resetBreadcrumb());
    history.push("/dashboard");
  }, [dispatch, history]);

  // Add entity to breadcrumb
  const addEntityToBreadcrumb = useCallback(
    (entityType, entityId, entityName, selectedTab) => {
      dispatch(addToBreadcrumb(entityType, entityId, entityName, selectedTab));
    },
    [dispatch]
  );

  // Remove entity from breadcrumb
  const removeEntityFromBreadcrumb = useCallback(
    (entityType, entityId) => {
      dispatch(removeFromBreadcrumb(entityType, entityId));
    },
    [dispatch]
  );

  // Clear all navigation
  const clearAllNavigation = useCallback(() => {
    dispatch(clearNavigation());
  }, [dispatch]);

  // Get selected tab for a specific entity
  const getSelectedTab = useCallback(
    (entityType, entityId) => {
      const entityKey = `${entityType}-${entityId}`;
      return entityContexts[entityKey]?.selectedTab || "";
    },
    [entityContexts]
  );

  // Check if an entity is in the breadcrumb
  const isEntityInBreadcrumb = useCallback(
    (entityType, entityId) => {
      return breadcrumbPath.some(
        (item) => item.type === entityType && item.id === entityId
      );
    },
    [breadcrumbPath]
  );

  // Check if an entity is in the history
  const isEntityInHistory = useCallback(
    (entityType, entityId) => {
      return breadcrumbHistory.some(
        (item) => item.type === entityType && item.id === entityId
      );
    },
    [breadcrumbHistory]
  );

  // Get entity position in breadcrumb
  const getEntityPosition = useCallback(
    (entityType, entityId) => {
      return breadcrumbPath.findIndex(
        (item) => item.type === entityType && item.id === entityId
      );
    },
    [breadcrumbPath]
  );

  // Get entity position in history
  const getEntityHistoryPosition = useCallback(
    (entityType, entityId) => {
      return breadcrumbHistory.findIndex(
        (item) => item.type === entityType && item.id === entityId
      );
    },
    [breadcrumbHistory]
  );

  // Navigate based on current route and update breadcrumb accordingly
  const syncWithRoute = useCallback(
    (currentRoute) => {
      // Parse the current route to understand where we are
      const pathSegments = currentRoute.split("/").filter(Boolean);

      // Handle dashboard route
      if (pathSegments.length === 1 && pathSegments[0] === "dashboard") {
        dispatch(syncWithRouteAction("dashboard", "dashboard", "Dashboard", ""));
        return;
      }

      // Reset breadcrumb for root navigation pages
      const rootPages = [
        "clients", 
        "portfolio", 
        "program", 
        "projects", 
        "resources", 
        "roles",
        // "shifts",
        "resource-planner",
        "timesheet",
        "settings", 
        "meetings", 
        "meeting",
        "meetings-registry",
        "invoiceRegistry", 
        "purchaseOrders", 
        "activity",
        "account",
        "scopes",
        "risks",
        "changeRequest",
        "changeRequests",
        "communication",
        "communications",
        "budget",
        "budgets",
        "kanban",
        "kanbanBoard",
        "makeSure",
        "make-sure",
        "ai-create",
        "timeline",
        "resourcePlanner",
        "cashFlow",
        "task",
        "milestone",
        "allocatedResources",
        "authentication"
      ];

      if (pathSegments.length === 1 && rootPages.includes(pathSegments[0])) {
        // We're at a root page like /clients, /projects, etc.
        dispatch(resetBreadcrumb());
        return;
      }

      // Parse entity routes like /portfolio/123/overview or /portfolio/123/details
      if (pathSegments.length >= 3) {
        const entityType = pathSegments[0];
        const entityId = pathSegments[1];
        const subPath = pathSegments[2];

        // Common entity tab paths
        const validTabs = ["overview", "details", "tasks", "resources", "timeline", "risks", "budget", "communication"];
        
        if (validTabs.includes(subPath)) {
          // We're at an entity tab page
          // Map route entity types to navigation entity types
          const entityTypeMap = {
            "clients": "Clients",
            "portfolio": "Portfolios", 
            "program": "Programs",
            "projects": "Projects",
            "scopes": "ScopeItems"
          };

          // First check if this entity already exists in breadcrumb history with any type
          const existingEntity = breadcrumbHistory.find(
            (item) => item.id.toString() === entityId.toString()
          );
          
          // Use existing entity type if found, otherwise use mapped type
          const finalEntityType = existingEntity ? existingEntity.type : (entityTypeMap[entityType] || entityType);
          
          // Only proceed if we have the proper entity name from breadcrumb history
          // This prevents creating breadcrumb items with generic names like "Projects 123"
          if (existingEntity) {
            // Only dispatch sync action if this is not already the current entity
            // This prevents duplicate breadcrumb items when RouteSync runs after navigation
            const isCurrentEntity = currentEntity && 
              currentEntity.type === finalEntityType && 
              currentEntity.id.toString() === entityId.toString();

            if (!isCurrentEntity) {
              // Dispatch sync action to update breadcrumb state
              dispatch(syncWithRouteAction(finalEntityType, entityId, existingEntity.name, subPath));
            }
          }
        }
      }
    },
    [dispatch, entityContexts, breadcrumbHistory, currentEntity]
  );

  // Get breadcrumb navigation chain for display
  const getBreadcrumbChain = useCallback(() => {
    return breadcrumbPath.map((item) => ({
      ...item,
      selectedTab: getSelectedTab(item.type, item.id),
    }));
  }, [breadcrumbPath, getSelectedTab]);

  // Get full navigation history chain
  const getNavigationHistoryChain = useCallback(() => {
    return breadcrumbHistory.map((item, index) => ({
      ...item,
      selectedTab: getSelectedTab(item.type, item.id),
      isCurrentPosition: index === currentBreadcrumbIndex,
      isPast: index < currentBreadcrumbIndex,
      isFuture: index > currentBreadcrumbIndex,
    }));
  }, [breadcrumbHistory, currentBreadcrumbIndex, getSelectedTab]);

  return {
    // Navigation state
    breadcrumbPath,
    breadcrumbHistory,
    currentBreadcrumbIndex,
    currentEntity,
    entityContexts,
    breadcrumbItems,
    canGoBack,
    canGoForward,
    parentEntity,
    isOnDashboard,
    navigationError,
    navigationLoading,
    nextEntity,
    previousEntity,
    forwardOptions,

    // Navigation actions
    navigateTo,
    navigateBackTo,
    navigateForward,
    navigateBackward,
    navigateToIndex,
    updateTab,
    navigateDirectly,
    navigateToDashboard,
    addEntityToBreadcrumb,
    removeEntityFromBreadcrumb,
    clearAllNavigation,

    // Utility functions
    getSelectedTab,
    isEntityInBreadcrumb,
    isEntityInHistory,
    getEntityPosition,
    getEntityHistoryPosition,
    syncWithRoute,
    getBreadcrumbChain,
    getNavigationHistoryChain,

    // Convenience properties
    breadcrumbLength: breadcrumbPath.length,
    historyLength: breadcrumbHistory.length,
    currentTab: currentEntity
      ? getSelectedTab(currentEntity.type, currentEntity.id)
      : "",
    hasNavigation: breadcrumbPath.length > 0,
    hasHistory: breadcrumbHistory.length > 0,
    isAtHistoryEnd: currentBreadcrumbIndex === breadcrumbHistory.length - 1,
    isAtHistoryStart: currentBreadcrumbIndex === 0,
  };
};

export default useNavigation;
